import { message } from "antd";
import axios from "axios";
import config from "../../../config.json";
import {
  postRequest,
  getRequest,
  patchRequest,
  formatRequestError,
} from "../../../utils/requests";
import { getToken } from "../../../utils/token";

// Получение заявок по типу (BOOKING или PRODUCTION)
export const fetchSuppliesByType = (
  type = "BOOKING",
  query = "",
  pageNumber = 1,
  pageSize = 1000,
  setLeads,
  setTotalPages,
  setIsLoading
) => {
  setIsLoading(true);

  const url = `${config.service}/api/equipments/active?type=${type}&page=${pageNumber}&size=${pageSize}&query=${encodeURIComponent(query)}`;

  const response = getRequest(url);

  response
    .then((data) => {
      setLeads(data.data?.application || []);
      setTotalPages(data.data?.totalPages || 0);
    })
    .catch((err) => {
      console.error("Ошибка загрузки заявок:", err);
      message.error("Ошибка загрузки заявок");
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const fetchActiveOrdersAsync = async (type = "ALL", page = 1, size = 20, query = "", purchaseStatus = "") => {
  const statusParam = purchaseStatus ? `&purchaseStatus=${encodeURIComponent(purchaseStatus)}` : "";
  const url = `${config.service}/api/equipments/active?type=${type}&page=${page}&size=${size}&query=${encodeURIComponent(query)}${statusParam}`;
  try {
    const res = await getRequest(url);
    return res.data;
  } catch (err) {
    console.error(`Ошибка загрузки заявок ${type}:`, err);
    throw err;
  }
};

export const fetchActiveTotalsAsync = async () => {
  const url = `${config.service}/api/equipments/active-totals`;
  try {
    const res = await getRequest(url);
    return res.data;
  } catch (err) {
    console.error("Ошибка загрузки active-totals:", err);
    throw err;
  }
};

// Устаревший метод для обратной совместимости
export const fetchSuppliesLeads = (
  query,
  pageNumber,
  pageSize,
  setLeads,
  setTotalPages,
  setIsLoading
) => {
  fetchSuppliesByType("BOOKING", query, pageNumber, pageSize, setLeads, setTotalPages, setIsLoading);
};

// Калькуляция комплектующих для заявки
export const fetchSuppliesLeadsCalc = (specId, setData, setIsLoading) => {
  setIsLoading(true);

  const url = `${config.service}/api/equipments/calc/${specId}`;

  const response = getRequest(url);

  response
    .then((data) => {
      setData(data.data || []);
    })
    .catch((err) => {
      console.error("Ошибка загрузки калькуляции:", err);
    })
    .finally(() => {
      setIsLoading(false);
    });
};

// Итого комплектующих по типу
export const fetchSuppliesTotalByType = (type = "PRODUCTION", setData, setIsLoading) => {
  setIsLoading(true);

  const url = `${config.service}/api/equipments/total-by-type?type=${type}`;

  const response = getRequest(url);

  response
    .then((data) => {
      setData(data.data || []);
    })
    .catch((err) => {
      console.error("Ошибка загрузки итогов:", err);
    })
    .finally(() => {
      setIsLoading(false);
    });
};

// Устаревший метод
export const fetchSuppliesTotal = (setData, setIsLoading) => {
  fetchSuppliesTotalByType("PRODUCTION", setData, setIsLoading);
};

// Генерация PDF закупки
export const generateSuppliesLeadsFile = (body, setIsLoading) => {
  setIsLoading(true);

  const url = `${config.service}/api/equipments/file`;

  postRequest(url, body, { responseType: "blob" })
    .then((res) => {
      const blobUrl = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = blobUrl;

      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`;
      link.download = `Zakupka_${dateStr}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      message.success("PDF успешно сгенерирован");
    })
    .catch((err) => {
      message.error(formatRequestError("Не удалось сгенерировать документ", err));
    })
    .finally(() => {
      setIsLoading(false);
    });
};

// Получение заявок со склада
export const fetchSuppliesWarehouse = (
  query,
  page,
  size,
  setData,
  setTotalPages,
  setIsLoading
) => {
  setIsLoading(true);

  const url = `${config.service}/api/equipments/storage?page=${page}&size=${size}&query=${encodeURIComponent(query)}`;

  const response = getRequest(url);

  response
    .then((data) => {
      setData(data.data?.application || []);
      setTotalPages(data.data?.totalPages || 0);
    })
    .finally(() => {
      setIsLoading(false);
    });
};

// Пометить заявки как закупленные
export const setAccessoriesPurchaseStatus = async (applicationIds) => {
  const url = `${config.service}/api/equipments/accessories-purchase-status`;

  await patchRequest(url, { applicationIds });
};

// ─── Новые функции для интеграции страницы «Снабжения» ─────────────────────

// Обновить статус закупки (NOT_PURCHASED / PURCHASED / SHIPPED) с логикой списания
export const updateOrderPurchaseStatus = async (
  applicationIds,
  status,
  login = null
) => {
  const url = `${config.service}/api/equipments/accessories-purchase-status`;

  const res = await patchRequest(url, { applicationIds, status, login });
  return res.data; // { success: true, stock: [...] }
};

// Получить остатки склада
export const fetchWarehouseStock = async (setData, setIsLoading) => {
  if (setIsLoading) setIsLoading(true);

  const url = `${config.service}/api/equipments/warehouse-stock`;

  try {
    const res = await getRequest(url);
    if (setData) setData(res.data || []);
    return res.data || [];
  } catch (err) {
    console.error("Ошибка загрузки остатков склада:", err);
    return [];
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

// Получить историю закупок за N месяцев (для расчёта медианы)
export const fetchPurchaseHistory = async (months = 12) => {
  const url = `${config.service}/api/equipments/purchase-history?months=${months}`;

  try {
    const res = await getRequest(url);
    return res.data || [];
  } catch (err) {
    console.error("Ошибка загрузки истории закупок:", err);
    return [];
  }
};

// Получить статусы сформированных периодов (PDF)
export const fetchPurchasePeriodStatuses = async () => {
  const url = `${config.service}/api/equipments/purchase-period-status`;
  const response = await getRequest(url);
  return response.data;
};

// Создать/Обновить статус периода после формирования PDF
export const createPurchasePeriodStatus = async (
  periodKey,
  dateFrom,
  dateTo,
  items
) => {
  const url = `${config.service}/api/equipments/purchase-period-status`;
  const response = await postRequest(url, {
    periodKey,
    dateFrom,
    dateTo,
    items,
  });
  return response.data;
};

// ─── Новые функции для раздела «Склад» ─────────────────────────────────────

// Ручной приход товара на склад
export const addWarehouseIncoming = async (materialName, quantity, date, comment) => {
  const url = `${config.service}/api/equipments/warehouse-incoming`;
  const res = await postRequest(url, { materialName, quantity, date, comment });
  return res.data; // { success, movement, stock }
};

// Получить журнал операций (поступления/списания)
export const fetchWarehouseMovements = async (page = 1, size = 50) => {
  const url = `${config.service}/api/equipments/warehouse-movements?page=${page}&size=${size}`;
  try {
    const res = await getRequest(url);
    return res.data || { movements: [], total: 0, totalPages: 0 };
  } catch (err) {
    console.error("Ошибка загрузки журнала операций:", err);
    return { movements: [], total: 0, totalPages: 0 };
  }
};

// Обновить Min/Max пороги и сроки доставки для позиции
export const updateWarehouseMinMax = async (materialName, minStock, maxStock, deliveryDays) => {
  const url = `${config.service}/api/equipments/warehouse-min-max`;
  const res = await patchRequest(url, { materialName, minStock, maxStock, deliveryDays });
  return res.data;
};

// Обновить остаток напрямую (инлайн-ввод)
export const setWarehouseStockDirect = async (materialName, inStock, login) => {
  const url = `${config.service}/api/equipments/warehouse-stock-set`;
  const res = await patchRequest(url, { materialName, inStock, login });
  return res.data; // { success, item, stock }
};

export const deleteWarehouseStockEntry = async (id, login) => {
  const url = `${config.service}/api/equipments/warehouse-stock/${id}`;
  const res = await axios.delete(url, {
    data: { login },
    headers: { Authorization: `Bearer ${getToken()}` }
  });
  return res.data;
};

// Получить фактический расход за последние 90 дней по каждому материалу (OUTGOING)
// Возвращает [{ materialName, totalOutgoing, movementsCount, lastMovementDate }]
export const fetchConsumptionForecast = async () => {
  const url = `${config.service}/api/equipments/consumption-forecast`;
  try {
    const res = await getRequest(url);
    return res.data || [];
  } catch (err) {
    console.error("Ошибка загрузки прогноза потребления:", err);
    return [];
  }
};

